import { doc, onSnapshot } from "firebase/firestore";
import { resolverAppConfig, type AppConfig } from "@nhomi/core";
import { db } from "./ambiente";

/* ============================================================
   Tetos e limites do produto, editáveis no painel sem publicar o app

   Quem lê com desconfiança é o core — o mesmo `resolverAppConfig` que o
   servidor usa, para os dois lados chegarem no mesmo número.

   ESTE OUVINTE JÁ MORREU EM PRODUÇÃO, e em silêncio. A regra do
   Firestore exige estar logado (`config/{doc}`: `allow read: if
   signedIn()`), e ele era montado na abertura do app, ANTES de o
   Firebase Auth terminar de restaurar a sessão do IndexedDB. No celular
   isso leva alguns décimos de segundo — o bastante. O que acontecia:

     - a leitura ia sem token e voltava `permission-denied`;
     - o ouvinte MORRE nesse erro e não volta sozinho quando o login
       chega;
     - o `catch` devolvia o padrão do core, então a tela seguia com
       número, só que o errado.

   O sintoma não parecia bug de configuração: o painel dizia 2
   estimativas por dia, o servidor recusava na terceira, e a tela
   prometia 5 — porque 5 é o padrão do core. Reproduzido no emulador:
   com o login resolvendo em 400ms, o ouvinte leva `permission-denied` e
   nunca mais recebe dado.

   Daí as três mudanças aqui: quem monta só monta COM SESSÃO (ver
   `lib/plano.tsx`), a falha é RECLAMADA no console em vez de sumir, e
   ela volta a tentar em vez de desistir calada.
   ============================================================ */

/** Espera entre tentativas. Cresce, e desiste antes de virar martelo. */
const ESPERAS = [1_000, 3_000, 8_000, 20_000];

/**
 * Escuta `config/app`.
 *
 * NÃO devolve o padrão quando falha, e isso é de propósito: quem chama
 * já nasce com o padrão, então avisar "falhou" com o padrão só serviria
 * pra apagar um valor bom que já tinha chegado. Configuração velha é
 * melhor que configuração inventada.
 */
export function watchAppConfig(cb: (config: AppConfig) => void): () => void {
  let vivo = true;
  let desinscrever: (() => void) | null = null;
  let relogio: ReturnType<typeof setTimeout> | null = null;
  let tentativa = 0;

  const montar = () => {
    if (!vivo) return;
    desinscrever = onSnapshot(
      doc(db, "config", "app"),
      (s) => {
        tentativa = 0;      // chegou dado: o orçamento de tentativas volta
        cb(resolverAppConfig(s.data()));
      },
      (err) => {
        /* Barulho de propósito. Este ouvinte silencioso custou um bug em
           produção que ninguém conseguia nomear — os limites da tela
           simplesmente não eram os do painel. */
        console.warn("[nhomi] não consegui ler config/app:", err);
        desinscrever = null;
        const espera = ESPERAS[tentativa];
        if (espera === undefined) return;   // desistiu: fica no que já tem
        tentativa += 1;
        relogio = setTimeout(montar, espera);
      },
    );
  };

  montar();

  return () => {
    vivo = false;
    if (relogio) clearTimeout(relogio);
    desinscrever?.();
  };
}
